import { useGlobalContext } from "../../utils/GlobalContext";
import { IconGlobe } from "@hawkins/assets";
import { IconChevronDown } from "@hawkins/assets";
import cx from "classnames";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useId, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useKeyboardEvent } from "@react-hookz/web";

export const LanguageSwitcher = () => {
  const { switchableLocaleSettings, localeSettings } = useGlobalContext();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const localeOptionsLinkListId = useId();

  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      const handler = (e: MouseEvent) => {
        if (
          e.target instanceof HTMLElement &&
          !containerRef.current?.contains(e.target)
        ) {
          setOpen(false);
        }
      };
      document.body.addEventListener("click", handler);
      return () => document.body.removeEventListener("click", handler);
    }
  }, [open]);
  useKeyboardEvent("Escape", () => setOpen(false), []);

  if (switchableLocaleSettings.length < 2) {
    return null;
  }

  return (
    <div ref={containerRef} className="fixed bottom-4 right-4 z-50">
      <button
        ref={buttonRef}
        aria-expanded={open}
        aria-controls={localeOptionsLinkListId}
        onClick={() => setOpen((o) => !o)}
        className={cx(
          "transition-colors peer text-neutral-cream hover:text-neutral-black focus-visible:text-neutral-black bg-neutral-cream/15 hover:bg-neutral-cream focus-visible:bg-neutral-cream p-[14px] rounded-lg flex gap-2 items-center outline-0 backdrop-blur-sm",
          open && "!text-neutral-black"
        )}
      >
        <IconGlobe height={14} width={14} />
        <span className="text-button-small">{localeSettings.localName}</span>
        <IconChevronDown
          height={12}
          width={12}
          className={cx("transition-transform", open && "rotate-180")}
        />
      </button>

      <div
        id={localeOptionsLinkListId}
        aria-hidden={!open}
        className={cx(
          "overflow-hidden transition-colors absolute min-w-full bottom-0 right-0 rounded-lg -z-10 text-neutral-black text-button-small peer-hover:bg-neutral-cream",
          // This is to account for the size of the chevron
          open && "bg-neutral-cream"
        )}
      >
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{
                height: 0,
                width: buttonRef.current?.clientWidth ?? 0,
              }}
              animate={{ height: "auto", width: "auto", opacity: 1 }}
              exit={{ height: 0, width: buttonRef.current?.clientWidth ?? 0 }}
            >
              <ul className="px-2 pt-2">
                {switchableLocaleSettings.map((l) => {
                  if (l.id === localeSettings.id) return null;

                  const sansLocalePath = location.pathname
                    .split("/")
                    .filter(Boolean);
                  sansLocalePath.shift();
                  const href =
                    "/" +
                    [l.id.toLocaleLowerCase(), ...sansLocalePath].join("/");

                  return (
                    <li key={l.id}>
                      {/* Changing the locale route is the rare time where we need to do a native refresh */}
                      {/* eslint-disable-next-line react/forbid-elements */}
                      <a
                        href={href}
                        className="transition-colors flex gap-2 items-center justify-end whitespace-nowrap py-2 px-[26px] rounded-lg hover:bg-brand-red focus-visible:bg-brand-red hover:text-neutral-cream focus-visible:text-neutral-cream outline-none"
                      >
                        <span>{l.localName}</span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
        {/**
         * The reason this uses a spacer element and not just bottom padding on the parent
         * is because this makes the animation slightly smoother by not having any text appear
         * from beneath or to the left of the button if it is significantly longer than the
         * content in the button
         */}
        <div
          style={{ height: buttonRef.current?.clientHeight }}
          className="w-full bg-[inherit] z-10 relative"
        />
      </div>
    </div>
  );
};
